import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Trophy, Medal, Award } from 'lucide-react';
import { fmtCurrency } from '../lib/utils';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const METRICS = [
  { key: 'revenue', label: 'Top Revenue', fmt: fmtCurrency },
  { key: 'outreach', label: 'Top Outreach', fmt: (v) => v },
  { key: 'meetings', label: 'Top Meetings', fmt: (v) => v },
  { key: 'media_partners', label: 'Top Media Partners', fmt: (v) => v },
  { key: 'conversion_rate', label: 'Top Conversion', fmt: (v) => `${v}%` },
];

export default function LeaderboardPage() {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    api.get(`/analytics/leaderboard?period=${period}`).then((r) => setRows(r.data));
  }, [period]);

  const sorted = [...rows].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
  const fmt = METRICS.find((m) => m.key === metric).fmt;

  const trophyFor = (idx) => {
    if (idx === 0) return <Trophy className="w-5 h-5 text-amber-400" />;
    if (idx === 1) return <Medal className="w-5 h-5 text-slate-400" />;
    if (idx === 2) return <Award className="w-5 h-5 text-amber-700" />;
    return <span className="text-sm font-bold text-slate-400">{idx + 1}</span>;
  };

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList>
            <TabsTrigger value="monthly" data-testid="period-monthly">Monthly</TabsTrigger>
            <TabsTrigger value="quarterly" data-testid="period-quarterly">Quarterly</TabsTrigger>
            <TabsTrigger value="annual" data-testid="period-annual">Annual</TabsTrigger>
          </TabsList>
        </Tabs>

        <Tabs value={metric} onValueChange={setMetric}>
          <TabsList className="flex-wrap h-auto">
            {METRICS.map((m) => (
              <TabsTrigger key={m.key} value={m.key} data-testid={`metric-${m.key}`}>
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {sorted.slice(0, 3).map((r, i) => (
          <div
            key={r.user_id}
            className={`relative rounded-2xl p-6 border ${i === 0 ? 'bg-navy text-white border-navy' : 'bg-white border-slate-100'} shadow-sm`}
            data-testid={`podium-${i}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] font-semibold tracking-[0.12em] uppercase opacity-70">#{i + 1} Place</div>
              {trophyFor(i)}
            </div>
            <div className={`font-heading text-xl font-bold ${i === 0 ? 'text-white' : 'text-navy'}`}>{r.full_name}</div>
            <div className={`text-xs ${i === 0 ? 'text-white/60' : 'text-slate-500'} uppercase tracking-wider mt-0.5`}>
              {r.role.replace('_', ' ')}
            </div>
            <div className={`font-heading text-4xl font-bold mt-4 ${i === 0 ? 'text-magenta' : 'text-navy'}`}>
              {fmt(r[metric])}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Rank</th>
              <th className="text-left font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Team Member</th>
              <th className="text-right font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Revenue</th>
              <th className="text-right font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Outreach</th>
              <th className="text-right font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Meetings</th>
              <th className="text-right font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Partners</th>
              <th className="text-right font-heading text-xs uppercase tracking-wider text-slate-500 px-6 py-3">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-${r.user_id}`}>
                <td className="px-6 py-4">{trophyFor(i)}</td>
                <td className="px-6 py-4">
                  <div className="font-semibold text-sm text-navy">{r.full_name}</div>
                  <div className="text-xs text-slate-500 capitalize">{r.role.replace('_', ' ')}</div>
                </td>
                <td className="px-6 py-4 text-right font-semibold text-navy">{fmtCurrency(r.revenue)}</td>
                <td className="px-6 py-4 text-right text-sm">{r.outreach}</td>
                <td className="px-6 py-4 text-right text-sm">{r.meetings}</td>
                <td className="px-6 py-4 text-right text-sm">{r.media_partners}</td>
                <td className="px-6 py-4 text-right font-semibold text-sm">{r.conversion_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
